import { useState, useEffect, useRef, use, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { CiSearch } from "react-icons/ci";
import { get, limitToLast, off, onValue, query, ref, remove, set, update } from "firebase/database";
import { database } from "../constants/firebase";
import { useCommunities, useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import StayInLoopBottomSheet from "../components/StayInLoopBottomSheet";
import MobileHeader from "../components/MobileHeader";
import Explore from "../components/Explore";
import CommunitiesList from "../components/CommunitiesList";
import { getMonthKey } from "../utils/utils";
import { useLocation } from "react-router";
import MyProgress from "../components/MyProgress";
import MonthlyChallenge from "../components/MonthlyChallenge";
import FilterBottomSheet from "../components/FilterBottomSheet";
import { MdFilterList } from "react-icons/md";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";

const Home = () => {
  const navigate = useMoveTo();
  const { currentUser } = useUser();
  const [activeTab, setActiveTab] = useState("Communities");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCommunities, setFilteredCommunities] = useState([]);
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [sortType, setSortType] = useState("communities");
  const { communities, setCommunities, loading, setLoading } = useCommunities();
  const [progressTab, setProgressTab] = useState("progress"); // Add this line

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const location = useLocation();
  const [selectedNiche, setSelectedNiche] = useState("all");
  const debounceRef = useRef(null);
  const messageListenersRef = useRef({});
  const [challenge, setChallenge] = useState(null);
  const [isFilterSheet, setIsFilterSheet] = useState(false);


  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      setSortType("people");
    }
  }, [location.state]);


  const fetchChallenge = async () => {
    const monthKey = getMonthKey();
    const snap = await get(ref(database, `Challenges/${monthKey}`));

    if (snap.exists()) {
      setChallenge(snap.val());
    } else {
      setChallenge(null);
    }
  };

  useEffect(() => {
    fetchChallenge();
  }, []);


  useEffect(() => {
    if (!currentUser?.uid) return;

    const usersRef = ref(database, "Users");

    const unsubscribe = onValue(usersRef, (snapshot) => {
      try {
        const data = snapshot.val() || {};

        const usersList = Object.entries(data)
          .filter(([id, user]) => user?.email)
          .map(([id, user]) => {

            const myData = data[currentUser.uid] || {};

            // ✅ Check if friend
            const isFriend = !!myData.friends?.[id];

            // ✅ Check if I sent request
            const iSentRequest = myData.friendRequests?.[id] === "sent";

            // ✅ Check if they sent request
            const theySentRequest = user.friendRequests?.[currentUser.uid] === "sent";

            let requestStatus = null;

            if (iSentRequest) requestStatus = "sent";
            else if (theySentRequest) requestStatus = "received";

            return {
              id,
              ...user,
              isFriend,
              requestStatus,
            };
          })

          .sort((a, b) => {
            const timeA = a.createdAt ? Number(a.createdAt) : 0;
            const timeB = b.createdAt ? Number(b.createdAt) : 0;
            return timeB - timeA;
          });

        setUsers(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
        setFilteredUsers([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    let filteredComms = [...communities];
    let filteredUsr = [...users];

    // 🔍 SEARCH FILTER
    if (searchQuery) {
      if (
        activeTab === "Communities" ||
        (activeTab === "Explore" && sortType === "communities")
      ) {
        filteredComms = filteredComms.filter(
          (community) =>
            community?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            community?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            community?.hostName?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (
        activeTab === "Explore" &&
        (sortType === "people" || sortType === "new")
      ) {
        filteredUsr = filteredUsr.filter(
          (user) =>
            user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user?.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user?.bio?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
    }

    // 🎯 NICHE FILTER (NEW 🔥)
    if (selectedNiche !== "all") {
      // Communities niche filter
      filteredComms = filteredComms.filter(
        (community) =>
          community?.niche?.toLowerCase() === selectedNiche.toLowerCase()
      );

      // Users niche filter
      filteredUsr = filteredUsr.filter(
        (user) =>
          user?.niche?.toLowerCase() === selectedNiche.toLowerCase()
      );
    }

    setFilteredCommunities(filteredComms);
    setFilteredUsers(filteredUsr);

  }, [searchQuery, selectedNiche, activeTab, communities, users, sortType]);


  const handleCloseSheet = () => {
    setIsOpenSheet(false);
  };


  const usersMap = useMemo(() => {
    return users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
  }, [users]);

  const processCommunities = ({
    communitiesData,
    messagesData,
    followedCommunities,
    currentUser,
    database,
    usersMap,
  }) => {
    const communitiesList = Object.entries(communitiesData).map(([id, community]) => {
      const messages = messagesData[id] || {};

      const lastMessage = Object.values(messages).reduce((latest, curr) => {
        return curr.timestamp > (latest?.timestamp || 0) ? curr : latest;
      }, null);

      const lastMessageTimestamp = community.lastActivity;

      const diffDays = lastMessageTimestamp
        ? Math.floor((Date.now() - lastMessageTimestamp) / (1000 * 60 * 60 * 24))
        : Infinity;

      const effectiveStreak =
        diffDays > 2 ? 0 : community.streakCount;

      // ✅ KEEP YOUR ORIGINAL DB RESET LOGIC
      if (
        currentUser.uid === community.hostId &&
        diffDays > 2 &&
        community.streakCount !== 0
      ) {
        update(ref(database, `Communities/${id}`), {
          streakCount: 0,
          freezeUsed: 0,
        });
      }

      const isActive = lastMessageTimestamp ? diffDays <= 2 : false;
      const hasMessages = Object.keys(messages).length > 0;

      // ✅ FIXED: Proper hasUnread logic for community messages
      let hasUnread = false;

      // Only check if there's a last message and it's not from current user
      if (lastMessage && lastMessage.senderId !== currentUser?.uid) {
        // Check if the current user has seen this message
        const isSeenByCurrentUser = lastMessage.isSeen?.[currentUser?.uid] === true;
        hasUnread = !isSeenByCurrentUser;

      }

      const isOwned = community.hostId === currentUser.uid;

      const host = usersMap?.[community.hostId];


      return {
        id,
        ...community,
        niche: host?.niche || null,
        isFollowed: !!followedCommunities[id],
        lastMessageTimestamp,
        isOwned,
        hasUnread,
        isActive: hasMessages && isActive,
        streakDays: effectiveStreak,
      };
    });

    // ✅ KEEP YOUR SORTING EXACTLY
    communitiesList.sort((a, b) => {
      if (a.isOwned !== b.isOwned) return b.isOwned - a.isOwned;
      if (a.isActive !== b.isActive) return b.isActive - a.isActive;
      if (b.streakDays !== a.streakDays) return b.streakDays - a.streakDays;
      return b.lastMessageTimestamp - a.lastMessageTimestamp;
    });

    return communitiesList;
  };



  const fetchLastMessages = async (communitiesData) => {
    const result = {};

    const communityIds = Object.keys(communitiesData);

    await Promise.all(
      communityIds.map(async (id) => {
        try {
          const q = query(
            ref(database, `communityMessages/${id}`),
            limitToLast(1)
          );

          const snap = await get(q);
          result[id] = snap.val() || {};
        } catch (err) {
          console.error("Error fetching last message for", id, err);
          result[id] = {};
        }
      })
    );

    return result;
  };

  useEffect(() => {
    if (!currentUser) return;

    const communitiesRef = ref(database, "Communities");
    const userFollowedRef = ref(database, `Users/${currentUser.uid}/joinedCommunities`);
    const userHostedRef = ref(database, `Users/${currentUser.uid}/hostedCommunities`);


    let communitiesData = {};
    let followedCommunities = {};
    let hostedCommunities = {};
    let messagesData = {};

    const updateCommunitiesState = () => {
      const result = processCommunities({
        communitiesData,
        messagesData,
        followedCommunities,
        currentUser,
        database,
        usersMap,
      });

      setCommunities(result);
    };

    const safeUpdate = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        updateCommunitiesState();
      }, 150);
    };

    const attachMessageListeners = (communitiesData) => {
      const ids = Object.keys(communitiesData);

      // 🔥 Remove old listeners
      Object.keys(messageListenersRef.current).forEach((id) => {
        if (!ids.includes(id)) {
          messageListenersRef.current[id]();
          delete messageListenersRef.current[id];
          delete messagesData[id];
        }
      });

      // ✅ Add new listeners
      ids.forEach((id) => {
        if (messageListenersRef.current[id]) return;

        const q = query(
          ref(database, `communityMessages/${id}`),
          limitToLast(1)
        );

        const unsubscribe = onValue(q, (snapshot) => {
          messagesData[id] = snapshot.val() || {};
          safeUpdate();
        });

        messageListenersRef.current[id] = unsubscribe;
      });
    };

    const fetchCommunitiesAndMessages = async () => {
      try {
        const snapshot = await get(communitiesRef);
        communitiesData = snapshot.val() || {};

        attachMessageListeners(communitiesData);

        // ✅ ONLY last message per community
        messagesData = await fetchLastMessages(communitiesData);

        const followedSnapshot = await get(userFollowedRef);
        followedCommunities = followedSnapshot.val() || {};

        const hostedSnapshot = await get(userHostedRef);
        hostedCommunities = hostedSnapshot.val() || {};

        safeUpdate();
      } catch (error) {
        console.error("Error fetching communities/messages:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommunitiesAndMessages();

    // Optional: you can still keep live listeners if you want real-time updates
    const unsubscribeCommunities = onValue(communitiesRef, (snapshot) => {
      communitiesData = snapshot.val() || {};
      safeUpdate();
    });

    const unsubscribeFollowed = onValue(userFollowedRef, (snapshot) => {
      followedCommunities = snapshot.val() || {};
      safeUpdate();
    });

    // ✅ Listen to hosted communities changes
    const unsubscribeHosted = onValue(userHostedRef, (snapshot) => {
      hostedCommunities = snapshot.val() || {};
      safeUpdate();
    });


    return () => {
      unsubscribeCommunities();
      unsubscribeFollowed();
      unsubscribeHosted();
      // 🔥 cleanup message listeners
      Object.values(messageListenersRef.current).forEach((unsub) => unsub());
      messageListenersRef.current = {};
    };
  }, [currentUser, usersMap]);


  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleJoinCommunity = async (communityId, isFollowed, memberCount) => {
    if (!currentUser || !communityId) return;

    try {
      const updates = {};
      const now = new Date().toISOString();

      const joining = !isFollowed;

      // Toggle membership
      updates[`Communities/${communityId}/members/${currentUser.uid}`] =
        joining ? true : null;

      updates[`Users/${currentUser.uid}/joinedCommunities/${communityId}`] =
        joining ? true : null;

      // 🔥 Store or remove join date
      updates[`Communities/${communityId}/memberJoinDates/${currentUser.uid}`] =
        joining ? now : null;

      // Update member count
      updates[`Communities/${communityId}/memberCount`] =
        joining
          ? (memberCount || 0) + 1
          : Math.max(0, (memberCount || 0) - 1);

      await update(ref(database), updates);

      // Optimistic UI update
      setCommunities((prev) =>
        prev.map((c) =>
          c.id === communityId
            ? {
              ...c,
              isFollowed: joining,
              memberCount: joining
                ? (c.memberCount || 0) + 1
                : Math.max(0, (c.memberCount || 0) - 1),
            }
            : c
        )
      );

    } catch (error) {
      console.error("Error updating join status:", error);
    }
  };


  const handleFollowUser = async (targetUser) => {
    if (!currentUser || !targetUser?.id) return;

    const updates = {};
    const myId = currentUser.uid;
    const targetId = targetUser.id;
    const now = new Date().toISOString();

    try {

      // 🔥 CASE 1: Already friends → UNFRIEND
      if (targetUser.isFriend) {
        updates[`Users/${myId}/friends/${targetId}`] = null;
        updates[`Users/${targetId}/friends/${myId}`] = null;

        updates[`Users/${myId}/friendRequests/${targetId}`] = null;
        updates[`Users/${targetId}/friendRequests/${myId}`] = null;

        await update(ref(database), updates);
        return;
      }

      // 🔥 CASE 2: Request already sent → CANCEL REQUEST
      if (targetUser.requestStatus === "sent") {
        updates[`Users/${myId}/friendRequests/${targetId}`] = null;
        updates[`Users/${targetId}/friendRequests/${myId}`] = null;

        await update(ref(database), updates);
        return;
      }

      // 🔥 CASE 3: Received request → ACCEPT FRIEND
      if (targetUser.requestStatus === "received") {

        updates[`Users/${myId}/friends/${targetId}`] = {
          createdAt: now
        };

        updates[`Users/${targetId}/friends/${myId}`] = {
          createdAt: now
        };

        updates[`Users/${myId}/friendRequests/${targetId}`] = null;
        updates[`Users/${targetId}/friendRequests/${myId}`] = null;

        await update(ref(database), updates);
        return;
      }

      // 🔥 CASE 4: Not friends → SEND REQUEST
      updates[`Users/${myId}/friendRequests/${targetId}`] = "sent";
      updates[`Users/${targetId}/friendRequests/${myId}`] = "received";

      await update(ref(database), updates);

    } catch (error) {
      console.error("Error updating friend status:", error);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
      {/* Mobile Header */}
      <MobileHeader />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <main className="flex-1 min-h-0 overflow-y-auto pt-4 md:pt-10 pb-20 px-6 bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
          {/* My Progress & Monthly Challenge Tabs */}
          <div className="border-b border-[#D9D9D9]/20">
            {/* Tab Headers */}
            <div className="flex items-center justify-center mb-3 md:mb-4 overflow-hidden border border-[#327691] w-max rounded-full mx-auto">
              <button
                onClick={() => setProgressTab("progress")}
                className={`py-2 px-4 
                  md:min-w-[180px] md:max-w-[180px] 
                    min-w-[160px] max-w-[160px]
                  font-semibold text-[13px] md:text-sm transition-all 
                  rounded-br-full rounded-tr-full relative ${progressTab === "progress"
                    ? "text-white bg-primary"
                    : "text-gray-500 dark:text-gray-400"
                  }`}
              >
                My Progress
              </button>
              <button
                onClick={() => setProgressTab("challenge")}
                className={`py-2 px-4 
                  md:min-w-[180px] md:max-w-[180px] 
                   min-w-[160px] max-w-[160px] 
                font-semibold text-[13px] md:text-sm transition-all rounded-bl-full rounded-tl-full relative ${progressTab === "challenge"
                    ? "text-white bg-primary"
                    : "text-gray-500 dark:text-gray-400"
                  }`}
              >
                Monthly Challenge
              </button>
            </div>

            {/* Tab Content */}
            <div className="mt-2">
              {progressTab === "progress" ? (
                <MyProgress currentUser={currentUser} />
              ) : (
                <MonthlyChallenge currentUser={currentUser} challenge={challenge} />
              )}
            </div>
          </div>

          {/* Tabs + Search */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mt-4 md:mt-6 gap-4">
            {/* First Buttons (Communities, Explore) */}
            <div className="inline-flex gap-2 justify-center lg:justify-start w-auto">
              {["Communities", "Explore"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-xl min-w-[140px] flex-1 font-semibold transition border border-[#327691] ${activeTab === tab
                    ? "bg-[#327691] text-white"
                    : "bg-transparent text-primary dark:text-white"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Second Buttons (Latest, Alphabetical) */}
            {activeTab === "Explore" && (
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start w-full">
                {["Communities", "People", "New"].map((sort) => (
                  <button
                    key={sort}
                    onClick={() => setSortType(sort.toLowerCase())}
                    className={`
          px-3 sm:px-4 py-1 sm:py-2 
          rounded-md md:rounded-xl
          min-w-[90px] sm:min-w-[120px] 
          flex-1 
          text-xs sm:text-base 
          font-semibold 
          transition 
          border border-[#327691] 
          ${sortType === sort.toLowerCase()
                        ? "bg-[#327691] text-white"
                        : "bg-transparent text-primary dark:text-white"
                      }
        `}
                  >
                    {sort}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 w-full lg:w-1/2">
              {/* Search Bar */}
              <div className="relative w-full ">
                <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                <input
                  type="text"
                  placeholder="Search name"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="rounded-xl pl-10 pr-10 py-2 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-[#262626] border border-gray-200 dark:border-none"
                />
                <button
                  onClick={() => setIsFilterSheet(true)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl"
                >
                  <HiOutlineAdjustmentsHorizontal />
                </button>
              </div>

            </div>

          </div>


          {isFilterSheet && (
            <FilterBottomSheet
              onClose={() => setIsFilterSheet(false)}
              selectedNiche={selectedNiche}
              setSelectedNiche={setSelectedNiche}
            />
          )}

          {/* Tab Content */}
          <div className="mt-4 md:mt-8">
            {loading ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-6 mt-6 md:mt-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="text-center animate-pulse">

                    {/* Circle shimmer */}
                    <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto rounded-full overflow-hidden">
                      <div className="absolute inset-0 rounded-full 
          bg-gradient-to-r 
          from-gray-300 via-gray-200 to-gray-300 
          dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 
          shimmer"
                      />
                    </div>

                    {/* Name shimmer */}
                    <div className="mt-3 flex justify-center">
                      <div className="h-3 w-16 md:w-20 rounded 
          bg-gradient-to-r 
          from-gray-300 via-gray-200 to-gray-300 
          dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 
          shimmer"
                      />
                    </div>

                  </div>
                ))}
              </div>
            ) :
              activeTab === "Communities" ? (
                // ✅ Show both owned (hosted) and joined communities, with owned first
                filteredCommunities.filter((c) => c.isOwned || c.isFollowed).length > 0 ? (
                  <CommunitiesList
                    communities={filteredCommunities.filter((c) => c.isOwned || c.isFollowed)}
                    navigate={navigate}
                  />
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400 mt-20">
                    No communities found.
                  </div>
                )
              ) : (
                <Explore
                  communities={filteredCommunities.filter((c) => c.hostId !== currentUser.uid)}
                  sortType={sortType}
                  users={filteredUsers}
                  currentUser={currentUser}
                  navigate={navigate}
                  handleJoinCommunity={handleJoinCommunity}
                  handleFollowUser={handleFollowUser}
                  selectedNiche={selectedNiche}
                />
              )}
          </div>


          {isOpenSheet && <StayInLoopBottomSheet onClose={handleCloseSheet} />}
        </main>
      </div>
    </div>
  );
};

export default Home;
